import { registerDefaultsForModule } from '@glu/theming';

/**
 * Create the defaults for the buttons module
 *
 * @param {boolean} legacy - legacy theme flag from glu theming
 * @param {Object} palette - palette hash from glu theming
 */
const themeDefaults = ({ legacy, palette }) => {
  const disabledColor = '#A1C2E1';
  return {
    ...legacy ? {
      cursor: 'pointer',
      fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
      outlineOffset: -2,
      breakpoint: 768,
      primary: {
        backgroundColor: palette.primary.normal,
        borderColor: palette.primary.normal,
        focusColor: palette.primary.veryDark,
        hoverColor: palette.primary.dark,
        textColor: palette.text.light,
        disabledBackground: disabledColor,
        disabledBorder: disabledColor,
        disabledColor: palette.text.light,
        spinnerBackground: disabledColor,
        spinnerBorder: disabledColor,
        spinnerColor: palette.text.light
      },
      secondary: {
        backgroundColor: palette.background,
        borderColor: palette.accent,
        focusColor: '#E6E6E6',
        focusBorderColor: '#ADADAD',
        hoverColor: '#EDEDED',
        textColor: palette.primary.normal,
        disabledBackground: palette.background,
        disabledBorder: '#E7E7E7',
        disabledColor,
        spinnerBackground: palette.background,
        spinnerBorder: '#E7E7E7',
        spinnerColor: disabledColor
      },
      tertiary: {
        backgroundColor: 'transparent',
        textDecoration: 'none',
        focusColor: 'transparent',
        focusTextDecoration: 'underline',
        hoverColor: 'transparent',
        hoverTextDecoration: 'underline',
        textColor: palette.primary.normal,
        disabledColor
      },
      normal: {
        borderRadius: 4,
        fontSize: 14,
        spinnerSize: 20,
        padding: [9, 12]
      },
      small: {
        borderRadius: 3,
        fontSize: 12,
        spinnerSize: 14,
        padding: [8, 10]
      },
      extraSmall: {
        borderRadius: 3,
        fontSize: 12,
        spinnerSize: 10,
        padding: [4, 5]
      },

      focusColor: palette.focus.normal,

      buttonToggleGroup: {
        borderRadius: 30,
        focusBorderColor: palette.focus.normal,
        focusBorderWidth: 2,
        focusPadding: '5px 24px',
        fontFamily: '\'Roboto Condensed\', Roboto, Helvetica, Arial, sans-serif',
        fontSize: 16,
        lineHeight: 1,
        toggleBackground: 'transparent',
        togglePadding: '6px 25px',
        textSize: '14px',
        selectedToggleBackground: palette.highlightBackground,
        fontColor: palette.primary.normal,
        toggleBorderColor: palette.primary.normal
      },

      menuButton: {
        borderRadius: 30,
        focusBorderColor: palette.focus.normal,
        focusBorderWidth: 2,
        focusPadding: '5px 24px',
        fontFamily: '\'Roboto Condensed\', Roboto, Helvetica, Arial, sans-serif',
        fontSize: 16,
        lineHeight: 1,
        background: 'transparent',
        padding: '3px 0px',
        textSize: '14px',
        dropdownToggleBorder: palette.grey.G400,
        dropdownOffset: 31,
        dropdownBackground: palette.background,
        dropdownRadius: 5,
        dropdownMenuHover: palette.highlightBackground,
        fontColor: palette.primary.normal,
        borderColor: palette.primary.normal
      }
    } : {
      cursor: 'pointer',
      fontFamily: '"Roboto Condensed", Roboto, Helvetica, Arial, sans-serif',
      breakpoint: 768,
      primary: {
        backgroundColor: palette.colors.lochmara,
        borderColor: palette.colors.lochmara,
        focusColor: palette.primary.dark,
        hoverColor: palette.primary.dark,
        textColor: palette.text.light,
        outlineReplacement: `0 0 3px 2px ${palette.focus.normal}`,
        disabledBackground: palette.primary.disabled,
        disabledBorder: palette.primary.disabled,
        disabledColor: palette.text.light,
        spinnerColor: palette.text.light
      },
      secondary: {
        backgroundColor: palette.background,
        borderColor: palette.colors.lochmara,
        focusColor: palette.highlightBackground,
        focusBorderColor: palette.colors.lochmara,
        hoverColor: palette.highlightBackground,
        textColor: palette.colors.lochmara,
        disabledBackground: palette.background,
        disabledBorder: palette.primary.disabled,
        disabledColor: palette.primary.disabled,
        spinnerColor: palette.colors.lochmara
      },
      tertiary: {
        backgroundColor: 'transparent',
        textDecoration: 'none',
        focusColor: 'transparent',
        focusTextDecoration: 'underline',
        hoverColor: 'transparent',
        hoverTextDecoration: 'underline',
        textColor: palette.colors.lochmara,
        padding: 0,
        disabledColor: palette.primary.disabled
      },
      normal: {
        borderRadius: 40,
        fontSize: 18,
        spinnerSize: 24,
        padding: [9, 25]
      },
      small: {
        borderRadius: 40,
        fontSize: 16,
        spinnerSize: 18,
        padding: [5, 25]
      },
      extraSmall: {
        borderRadius: 40,
        fontSize: 14,
        spinnerSize: 14,
        padding: [2, 20]
      },
      focusColor: palette.focus.normal,

      buttonToggleGroup: {
        borderRadius: 30,
        focusBorderColor: palette.focus.normal,
        focusBorderWidth: 2,
        focusPadding: '5px 24px',
        fontFamily: '\'Roboto Condensed\', Roboto, Helvetica, Arial, sans-serif',
        fontSize: 16,
        lineHeight: 1,
        toggleBackground: 'transparent',
        togglePadding: '6px 25px',
        textSize: '14px',
        selectedToggleBackground: palette.highlightBackground,
        fontColor: palette.colors.lochmara,
        toggleBorderColor: palette.colors.lochmara
      },

      menuButton: {
        borderRadius: 30,
        focusBorderColor: palette.focus.normal,
        focusBorderWidth: 2,
        focusPadding: '5px 24px',
        fontFamily: '\'Roboto Condensed\', Roboto, Helvetica, Arial, sans-serif',
        fontSize: 16,
        lineHeight: 1,
        background: 'transparent',
        padding: '3px 0px',
        textSize: '14px',
        dropdownToggleBorder: palette.grey.G400,
        dropdownOffset: 31,
        dropdownBackground: palette.background,
        dropdownRadius: 5,
        dropdownMenuHover: palette.highlightBackground,
        fontColor: palette.colors.lochmara,
        borderColor: palette.colors.lochmara
      }
    }
  };
};

let defaultsRegistered = false;

export default function registerButtonDefaults() {
  if (!defaultsRegistered) {
    registerDefaultsForModule('buttons', themeDefaults);
    defaultsRegistered = true;
  }
}
